import React from 'react';
import styled from 'styled-components';
import cn from 'classnames';
import { Localization } from 'connex-cds';
import { get } from 'lodash';
import { useCurrency } from '../../../../../useCurrency';
import style from './style';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useTicketContext } from '../../../../../TicketContext';
const { Translate } = Localization;

const Styled = styled.section`
  ${style}
`;

const testId = 'productsGrid';
const CMDSERIES = ['COMMAND_SERIES_V2', 'COMMAND_SERIES_V3'];

export const Products = ({ productsData, values }) => {
  const { getCurrency } = useCurrency();
  const { ticket } = useTicketContext();

  const originatorSystemType = React.useMemo(() => ticket?.originatorSystemType ?? '', [ticket?.originatorSystemType]);

  const products = productsData.map(product => {
    if (!product.priceSummary) {
      const pricedLineItems = values?.pricing?.pricedLineItems;
      const targetPricedLineItem = pricedLineItems?.filter(
        lineItem => lineItem?.item?.productRef === product.item?.productRef
      );
      product.priceSummary = get(targetPricedLineItem, ['0', 'priceSummary'], { unitPrice: 0 });
    }
    return product;
  });

  const calculateSubtotal = product => {
    if (product?.driverDidNotAdd) {
      return getCurrency(0);
    }

    const quantity = product?.quantity?.value ?? 0;
    const unitPrice = product?.priceSummary?.unitPrice ?? 0;
    const isNotDriverSellable = !Boolean(product?.item?.isDriverSellable);

    if (CMDSERIES.includes(originatorSystemType) && isNotDriverSellable) {
      const extendedPrice = product?.priceSummary?.extendedPrice ?? 0;
      return getCurrency(extendedPrice);
    }

    return getCurrency(quantity * unitPrice);
  };

  return (
    <Styled
      data-testid={testId}
      className={cn('products-table-section')}
      style={{
        overflowX: 'auto',
        width: '100%',
      }}
    >
      <Table>
        <TableHead>
          <TableRow data-testid={`${testId}-first-header-row`}>
            <TableCell colSpan={4} style={{ textAlign: 'center', 'font-weight': 'bold' }}>
              <Translate stringId="paymentProducts" data-testid="products-label" />
            </TableCell>
          </TableRow>

          <TableRow data-testid={`${testId}-second-header-row`}>
            <TableCell style={{ 'font-weight': 'bold' }}>
              <Translate stringId="product" data-testid="product-label" />
            </TableCell>
            <TableCell style={{ 'font-weight': 'bold' }}>
              <Translate stringId="quantity" data-testid="quantity-label" />
            </TableCell>
            <TableCell style={{ 'font-weight': 'bold', textAlign: 'right' }}>
              <Translate stringId="unitPrice" data-testid="unit-price-label" />
            </TableCell>
            <TableCell style={{ 'font-weight': 'bold', textAlign: 'right' }}>
              <Translate stringId="amount" data-testid="amount-label" />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {products?.map((product, index) => (
            <TableRow key={product.crn} data-testid={`${testId}-row-${index}`}>
              <TableCell
                data-testclass="cell-column-product"
                data-testid={`${testId}-row-${index}-column-product`}
                style={{ flexGrow: 2 }}
              >
                {product?.item?.name ?? product?.item?.description}
              </TableCell>
              <TableCell
                data-testclass="cell-column-quantity"
                data-testid={`${testId}-row-${index}-column-quantity`}
                style={{ flexGrow: 1 }}
              >
                {`${product?.driverDidNotAdd ? 0 : product?.quantity?.value ?? 0} ${product?.quantity?.uomCode}`}
              </TableCell>
              <TableCell
                data-testclass="cell-column-unitPrice"
                data-testid={`${testId}-row-${index}-column-unitPrice`}
                style={{ textAlign: 'right', flexGrow: 1 }}
              >
                {`${getCurrency(product?.priceSummary?.unitPrice || 0)}`}
              </TableCell>
              <TableCell
                data-testclass="cell-column-amount"
                data-testid={`${testId}-row-${index}-column-amount`}
                style={{ textAlign: 'right', flexGrow: 1 }}
              >
                {`${calculateSubtotal(product)}`}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Styled>
  );
};
